import { Injectable } from '@angular/core';
import { ActiveToast, ToastrService } from 'ngx-toastr';
import {
  EMPTY, Observable, TimeoutError, catchError, concatMap, defaultIfEmpty, defer, finalize,
  ignoreElements, of, take, tap, throwError, timeout, timer,
} from 'rxjs';

import { ProposalService, BatchDeclineRequest } from './proposal.service';
import { ProjectService } from './project.service';
import { ModalService } from './modal.service';
import { SiteDetail, buildBulkProjectDetail } from '../list/bulk-eligibility.util';
import {
  BATCH_DECLINE_FALLBACK, BATCH_DECLINE_IN_PROGRESS, BATCH_DECLINE_TIMEOUT_MS, BATCH_RELOAD_MAX_ATTEMPTS,
  BATCH_RELOAD_TIMEOUT_MS, BATCH_RESULT_NOT_SHOWN_MESSAGE, GATEWAY_CONFIRMING_MESSAGE, GATEWAY_POLL_DEADLINE_MS,
  GATEWAY_RELOAD_PENDING_MESSAGE, GATEWAY_RELOAD_WAITS_MS, RELOAD_FAILED_MESSAGE,
  applyBatchDeclineResult, isGatewayError, pendingDeclines,
} from '../list/batch-decline.util';

@Injectable({ providedIn: 'root' })
export class BatchDeclineService {
  constructor(
    private proposalService: ProposalService,
    private projectService: ProjectService,
    private modalService: ModalService,
    private toastr: ToastrService
  ) {}

  public declineAll(
    rawProject: any,
    rows: readonly SiteDetail[],
    declineFields: Omit<BatchDeclineRequest, 'proposalIds'>
  ): Observable<void> {
    return defer(() => {
      const stickyToasts: (ActiveToast<any> | null)[] = [];
      let outcomeShown = false;
      let resultReceived = false;
      let gatewayError = false;
      const sentIds = rows.map((row) => row.proposal.id);

      return defer(() => {
        stickyToasts.push(this.toastr.info(BATCH_DECLINE_IN_PROGRESS(rows.length), undefined, { disableTimeOut: true }));
        return this.proposalService.declineInvitationBatch({ proposalIds: sentIds, ...declineFields })
          .pipe(timeout(BATCH_DECLINE_TIMEOUT_MS));
      }).pipe(
        catchError((error) => {
          // A timeout is an unknown outcome, like a 504: the server may still be declining.
          gatewayError = error instanceof TimeoutError || isGatewayError(error?.status);
          if (gatewayError) {
            stickyToasts.push(this.toastr.error(
              `${BATCH_DECLINE_FALLBACK} ${GATEWAY_CONFIRMING_MESSAGE}`,
              undefined,
              { disableTimeOut: true }
            ));
          } else {
            this.toastr.error(BATCH_DECLINE_FALLBACK);
          }
          outcomeShown = true;
          return of(null);
        }),
        concatMap((response) => {
          // An HTTP error has already shown its toast. Anything else is answered
          // here, including a 200 with no body, which arrives as `null`.
          if (!outcomeShown) {
            const data = response?.data;
            const usable = data && typeof data === 'object' && Array.isArray(data.products);
            if (response && usable && (response.status === 'SUCCESS' || response.status === 'FAILURE')) {
              resultReceived = true;
              const { changedRows, summary } = applyBatchDeclineResult(rows, data, response.message, declineFields);
              if (changedRows.length) {
                const current = this.projectService.cachedProject(rawProject.projectId) ?? rawProject;
                this.projectService.updateProjectDataSubjectWithProjectDetail(buildBulkProjectDetail(current, changedRows));
              }
              this.modalService.toggleOpenBatchDeclineModal(summary);
            } else {
              this.toastr.error(response?.message || BATCH_DECLINE_FALLBACK);
            }
            outcomeShown = true;
          }
          return gatewayError
            ? this.pollUntilSettled(rawProject.projectId, sentIds)
            : this.reloadNormally();
        }),
        catchError((error) => {
          console.error(error);
          if (!outcomeShown) {
            // After the server answered with a result, the decline may have worked.
            this.toastr.error(resultReceived ? BATCH_RESULT_NOT_SHOWN_MESSAGE : BATCH_DECLINE_FALLBACK);
          }
          // A malformed result or cache-update failure must still end with a read.
          return this.reloadNormally();
        }),
        ignoreElements(),
        finalize(() => {
          for (const toast of stickyToasts) {
            toast?.toastRef.close();
          }
        })
      );
    });
  }

  /**
   * A timeout cancels the HTTP read and counts as a dropped attempt, like a read
   * a merge overtook; a failed read is not resent. The deadline stops resends
   * only: a reload already scheduled always runs, even if its timer fires late.
   */
  private reloadCurrent(deadline = Number.POSITIVE_INFINITY, attempt = 1): Observable<boolean> {
    return defer(() => {
      if (attempt > 1 && Date.now() > deadline) {
        return of(false);
      }
      return this.projectService.refetchCurrent().pipe(
        timeout(BATCH_RELOAD_TIMEOUT_MS),
        take(1),
        defaultIfEmpty(false),
        catchError((error) => error instanceof TimeoutError ? of(false) : throwError(() => error)),
        concatMap((applied) => applied || attempt >= BATCH_RELOAD_MAX_ATTEMPTS
          ? of(applied)
          : this.reloadCurrent(deadline, attempt + 1))
      );
    }).pipe(catchError(() => of(false)));
  }

  private reloadNormally(): Observable<boolean> {
    return this.reloadCurrent().pipe(tap((applied) => {
      if (!applied) {
        this.toastr.error(RELOAD_FAILED_MESSAGE);
      }
    }));
  }

  private pollUntilSettled(projectId: string, sentIds: readonly string[]): Observable<void> {
    return defer(() => {
      const deadline = Date.now() + GATEWAY_POLL_DEADLINE_MS;
      let anyApplied = false;
      let pendingNoticeShown = false;

      const poll = (index: number): Observable<void> => this.reloadCurrent(deadline).pipe(
        concatMap((applied) => {
          anyApplied = anyApplied || applied;
          // A failed or dropped read says nothing about whether the cached rows settled.
          const settled = applied && !pendingDeclines(
            sentIds,
            this.projectService.cachedStatuses(sentIds),
            // A search filters per site, so it can hide a sent product its project still lists.
            !!this.projectService.cachedProject(projectId) && !this.projectService.searchActive
          );
          const continues = !settled && index < GATEWAY_RELOAD_WAITS_MS.length && Date.now() < deadline;
          if (!applied && continues && !pendingNoticeShown) {
            this.toastr.error(GATEWAY_RELOAD_PENDING_MESSAGE);
            pendingNoticeShown = true;
          }
          // Later failures need no repeated notice: the vendor has already been told.
          if (!continues) {
            return EMPTY;
          }
          const wait = Math.min(GATEWAY_RELOAD_WAITS_MS[index], deadline - Date.now());
          return timer(wait).pipe(concatMap(() => poll(index + 1)));
        })
      );

      return poll(0).pipe(tap({ complete: () => {
        if (!anyApplied) {
          this.toastr.error(RELOAD_FAILED_MESSAGE);
        }
      } }));
    });
  }
}
